// ProfileSettingsPage.jsx
// Where each tenant configures their identity, company intelligence, and AI persona names.
// This is the onboarding page for new tenants and the settings page for existing ones.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useProfile } from '../context/UserProfileContext';
import { useAuth } from '../context/AuthContext';
import TrainingSection from './TrainingSection';
import DataTransferSection from './DataTransferSection';
import './ProfileSettingsPage.css';
import { apiFetch, startOAuthConnect } from '../lib/api';
import PromptEditorPanel from './PromptEditorPanel';
import PrivacySection from './PrivacySection';

// The mail data integrations the panel offers (P0.24 B5). One entry drives one card:
// status, connect, reconnect, disconnect all key on `id`, which is also the path
// segment under /api/auth/<id>/. Data connections, NOT sign-in providers — a user
// signed into DigitalMe with Google still connects Gmail here, and the panel says so.
//
// Adding a provider is one entry here plus its five handlers under api/auth/<id>/;
// nothing else in this file names a provider.
const MAIL_PROVIDERS = [
  {
    id: 'microsoft', name: 'Microsoft 365', logo: 'Ⓜ️',
    description: 'Enables: Outlook send/receive · Calendar view · SharePoint documents',
    disconnectWarning: 'Outlook, Calendar and SharePoint features will stop working until you reconnect.',
  },
  {
    id: 'google', name: 'Google', logo: 'G',
    description: 'Enables: Gmail send/receive',
    disconnectWarning: 'Gmail send and receive will stop until you reconnect.',
  },
];

// Display names for the OAuth outcome banner, keyed by the provider id the auth
// callbacks send. Zoho has no card (it is CRM, and possibly dead — TODO.md), but its
// callback can still land here.
const PROVIDER_LABELS = { ...Object.fromEntries(MAIL_PROVIDERS.map((p) => [p.id, p.name])), zoho: 'Zoho CRM' };

/**
 * One integration's card. `status` is null (not loaded), 'checking', or the status
 * endpoint's body; `busy` is null | 'connecting' | 'disconnecting'.
 *
 * Renders lastRefreshedAt, deliberately NOT expiresAt: that is the access token's
 * expiry, which the provider sets one hour out and the app refreshes silently — it
 * rendered as "Expires <today>", reading as "your connection dies today".
 * lastRefreshedAt answers the question a reader actually has: is this working now.
 */
function IntegrationCard({ provider, status, busy, error, onConnect, onDisconnect }) {
  const loaded = status && status !== 'checking';
  const connected = !!(loaded && status.connected);
  return (
    <div className="ps-integration-card" data-provider={provider.id}>
      <div className="ps-integration-logo">{provider.logo}</div>
      <div className="ps-integration-info">
        <h4 className="ps-integration-name">{provider.name}</h4>
        <p className="ps-integration-desc">{provider.description}</p>
        {status === 'checking' && (
          <span className="ps-integration-status ps-integration-status--checking">Checking connection...</span>
        )}
        {connected && (
          <span className="ps-integration-status ps-integration-status--connected">
            ✓ Connected — {status.email}
            {status.lastRefreshedAt && ` · Last synced ${relativeTime(status.lastRefreshedAt)}`}
          </span>
        )}
        {loaded && !connected && (
          <span className="ps-integration-status ps-integration-status--disconnected">Not connected</span>
        )}
      </div>
      <div className="ps-integration-action">
        {connected ? (
          <>
            <button type="button" onClick={onConnect} disabled={!!busy}
                    className="ps-integration-btn ps-integration-btn--reconnect">
              {busy === 'connecting' ? 'Starting…' : 'Reconnect'}
            </button>
            <button type="button" onClick={onDisconnect} disabled={!!busy}
                    className="ps-integration-btn ps-integration-btn--disconnect">
              {busy === 'disconnecting' ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </>
        ) : (
          <button type="button" onClick={onConnect} disabled={busy === 'connecting'}
                  className="ps-integration-btn ps-integration-btn--connect">
            {busy === 'connecting' ? 'Starting…' : 'Connect'}
          </button>
        )}
        {error && (
          <p className="ps-integration-error" role="alert">{error}</p>
        )}
      </div>
    </div>
  );
}

const SECTIONS = ['Identity', 'Company', 'AI Personas', 'Deal Settings', 'Training', 'Data', 'Integrations', 'Privacy'];

// Only owners and admins see the Users tab. This mirrors ELEVATED_ROLES in
// api/shared/auth-context.js — it is a courtesy that keeps the tab out of the way for
// everyone else, NOT a control. The enforcement is requireRole on
// /api/admin/users/*, which 403s a member regardless of what the UI renders.
// Exported for Dashboard's readiness banner (21.10f), which gates on the same
// rule. A third copy of this list is how the two drift; there are already two
// (here and api/shared/auth-context.js) and userApprovalGate.test.js pins the
// value precisely because that duplication is load-bearing.
export const ELEVATED_ROLES = ['owner', 'admin'];

/**
 * "3 minutes ago" for a timestamp. Used for the integration's last sync.
 *
 * Falls back to a date for anything older than a week — "eight days ago" is harder to
 * read than the date itself, and by then the exact age has stopped mattering.
 */
function relativeTime(iso) {
  // The falsy check is not redundant: new Date(null) is epoch 0, NOT NaN, so a null
  // timestamp would render "1/1/1970" instead of being caught. Only undefined and
  // unparseable strings give NaN.
  if (!iso) return 'unknown';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24)  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days <= 7)   return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ProfileSettingsPage() {
  const { profile, isLoading, updateProfile, resetProfile } = useProfile();
  const { role } = useAuth();
  const isElevated = ELEVATED_ROLES.includes(role);
  // Logs is OWNER-only, one notch stricter than the Users tab. Vercel and GitHub
  // logs are platform-wide — they carry request paths and other tenants' account
  // names — so the gate is requireOwner on every /api/admin/logs route, not requireRole.
  //
  // P0.33, 25 Oct 2026 — THE ACTIVITY SOURCE IS NOW SCOPED PER CALLER, which this
  // comment used to be the whole justification for. requireOwner proves the caller
  // owns SOME tenant and never compares that tenant to the rows returned, so
  // /api/admin/logs/activity handed every tenant's AI session summaries and import
  // filenames to any one tenant's owner. It now returns the caller's own tenant,
  // and the full cross-tenant view only for SYSTEM_ADMIN_EMAILS. The reasoning
  // above still holds for the vercel and github sources, which read operator
  // infrastructure rather than another tenant's data.
  // (Written without a "slash-star" in it: several tests strip block comments from this
  // file before asserting, and that sequence inside a line comment opened a phantom
  // block that swallowed 440 lines — P0.24 B5 found the guards half-blind.)
  // As with Users, hiding the tab is a courtesy; the API is the control.
  // P0.34 — `isOwner` removed: it existed solely to gate the Logs tab, which
  // has moved. `isElevated` stays, because the save path still uses it.
  // Data requests is for the deployment's SYSTEM administrator (SYSTEM_ADMIN_EMAILS),
  // not a tenant role — the queue spans tenants. /api/me/data-request says whether the
  // caller is one, so no admin endpoint is probed for every user. As with the other
  // gated tabs, hiding it is a courtesy; requireSystemAdmin is the control.
  // P0.34 — the isSystemAdmin probe and the open-data-request count moved to
  // AdminPage.jsx with the tabs they served. Two fewer requests on every load
  // of this page for every user, and one fewer place that has to know what
  // SYSTEM_ADMIN_EMAILS means.

  // P0.34 — Users, Logs, Platform and Data requests have MOVED to AdminPage.jsx
  // (`#admin`). This page configures a person's own environment; those four
  // administer the deployment, and the subtitle above — "Everything here shapes
  // how Digital Me works for you" — was never true of them.
  //
  // The new home is SYSTEM-ADMIN ONLY, which is a deliberate narrowing: Users
  // was reachable by any owner or admin and Logs by any owner. Asked and
  // confirmed 25 Oct 2026 with the regression named. No API gate changed, so
  // the UI now shows LESS than the endpoints permit — the safe direction.
  const sections = SECTIONS;
  // P0.34 — the pending-approval count moved to AdminPage.jsx with the Users
  // tab. It was still being FETCHED here after the tab left, and read only by a
  // useEffect dependency array — a request on every settings load for every
  // elevated user, feeding a badge that no longer rendered. Removed rather than
  // left: a live fetch whose result nothing displays is the kind of thing that
  // survives for years because it never fails visibly.
  // An OAuth return lands on `#profile/Integrations`, so the panel the user was on
  // when they clicked Connect is the one they come back to.
  const [activeSection, setActiveSection] = useState(() => {
    // P0.21 task 21.10f. This used to honour ONLY 'Integrations' — the OAuth
    // return path — so every other `#profile/<Section>` link silently landed on
    // Identity. The readiness banner's button deep-links to Company, and a
    // button that lands on the wrong tab leaves the user exactly where they
    // were: hunting for the field. Any KNOWN section is accepted now; anything
    // else still falls through to Identity.
    //
    // SECTIONS only — never the role-gated tabs. A member following a stale
    // `#profile/Users` link would otherwise select a tab that is not rendered
    // for them, and the panel area would come back blank.
    const section = (window.location.hash || '').replace(/^#/, '').split('/')[1];
    return SECTIONS.includes(section) ? section : 'Identity';
  });

  // P0.34 — a stale `#profile/Users` (bookmark, shared link, old release note)
  // now points at a tab this page no longer has. Without this it would fall
  // through to Identity silently, which reads as "the feature is gone" rather
  // than "it moved". Redirect to the real home instead, preserving the item.
  //
  // MOVED_TO_ADMIN maps the OLD tab name to the NEW section id — 'Data requests'
  // became 'Data-requests' because a hash fragment with a space in it is not
  // something a user can paste reliably.
  useEffect(() => {
    const MOVED_TO_ADMIN = {
      'Users': 'Users',
      'Logs': 'Logs',
      'Platform': 'Platform',
      'Data requests': 'Data-requests',
      'Data-requests': 'Data-requests',
    };
    const raw = (window.location.hash || '').replace(/^#/, '').split('/');
    if (raw[0] !== 'profile' || raw.length < 2) return;
    const target = MOVED_TO_ADMIN[decodeURIComponent(raw.slice(1).join('/'))];
    if (!target) return;
    // A full navigation, not a replaceState: Dashboard reads the view from the
    // fragment ONCE, in its initialiser, so rewriting the hash in place would
    // leave this page mounted showing Identity under an #admin URL.
    window.location.hash = `#admin/${target}`;
    window.location.reload();
  }, []);

  // ── Mobile tab strip ────────────────────────────────────────────────────────
  // Below --dm-bp-lg the nav is a horizontal scroller. Two things CSS cannot do
  // for it on its own:
  //
  //   1. Know whether it actually overflows. The right-edge fade must not appear
  //      on a desktop-width nav, or on a phone where every tab already fits, so
  //      the mask is gated on a class set from measurement here.
  //   2. Keep the active tab in view. An OAuth return lands on
  //      `#profile/Integrations` — the LAST tab — and without this the row opens
  //      scrolled to the far left showing "Identity" while the Integrations
  //      panel is what is rendered.
  const navRef = useRef(null);
  const activeTabRef = useRef(null);
  const [navScrollable, setNavScrollable] = useState(false);
  const [navAtEnd, setNavAtEnd] = useState(false);

  const syncNavScroll = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    // 1px of slack: scrollWidth/clientWidth are rounded from fractional layout
    // widths, so an exactly-fitting row can report a 0.5px overflow and fade for
    // no reason.
    const overflow = el.scrollWidth - el.clientWidth;
    setNavScrollable(overflow > 1);
    setNavAtEnd(el.scrollLeft >= overflow - 1);
  }, []);

  // Re-measure on mount and on resize — crossing the 900px breakpoint changes
  // the nav from a column to a row, and a column never overflows horizontally.
  // `sections` is re-measured too: the Users tab only appears once the role
  // resolves, and an eighth tab can be what tips the row into overflowing.
  useEffect(() => {
    syncNavScroll();
    window.addEventListener('resize', syncNavScroll);
    return () => window.removeEventListener('resize', syncNavScroll);
    // pendingCount dropped from the deps with the badge it re-measured for
    // (P0.34). sections.length still belongs here: the list is shorter now, but
    // it is still what decides whether the mobile strip overflows.
  }, [syncNavScroll, sections.length]);

  // `block: 'nearest'` is load-bearing: the default ('start') scrolls the PAGE
  // to put the nav at the top of the viewport, which on a phone throws the
  // header off-screen on first paint.
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    syncNavScroll();
  }, [activeSection, syncNavScroll]);

  // The connect outcome, read from the query string the callback set. Held in state and
  // the URL cleared immediately: leaving ?auth_error in the address bar means a refresh
  // re-shows a stale failure, and the message would outlive a successful retry.
  //
  // The provider rides along too (P0.23 A4): `auth_success` carries it on success and
  // the callbacks add `auth_provider` on failure. Before this the banner said
  // "Microsoft 365 connected" for any provider, which a second one (P0.24) would make
  // a lie on its first successful connect.
  const [authOutcome, setAuthOutcome] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const ok = q.get('auth_success');
    const err = q.get('auth_error');
    if (!ok && !err) return null;
    const provider = ok || q.get('auth_provider') || null;
    return ok ? { ok: true, provider } : { ok: false, provider, message: err };
  });

  useEffect(() => {
    if (!authOutcome) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('auth_success');
    url.searchParams.delete('auth_error');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, [authOutcome]);
  const [saved, setSaved] = useState(false);
  // True for the duration of the tenant-side POST, which re-derives up to four
  // sets and is measured in seconds, not milliseconds.
  const [saving, setSaving] = useState(false);
  const [localProfile, setLocalProfile] = useState(() => JSON.parse(JSON.stringify(profile)));

  // Re-sync the form once the provider has finished loading.
  //
  // `profile` is DEFAULT_PROFILE at mount — the provider starts at null and the
  // context substitutes the defaults — and the real values arrive later, from
  // localStorage and then from getSession(). The line above snapshots the MOUNT
  // value, so without this the form kept showing (and saving) the defaults.
  //
  // The visible symptom was a surname that saved and came back blank: the save
  // sent the whole mount snapshot, so it wrote lastName='Chitale' alongside
  // email='' and firstName='' from the defaults, and the next load's identity
  // seeder saw a blank email and reseeded over it.
  //
  // Fields the user has actually touched are never overwritten by a later
  // provider update. `isLoading` clears in the load effect's `finally`, which
  // runs BEFORE the async getSession() seed resolves — so a second update
  // arrives after the first sync, and a blunt "sync once" would either miss it
  // or clobber typing that happened in between.
  //
  // So: adopt provider values for every field the user has not edited, and keep
  // theirs for the ones they have.
  const touched = useRef(new Set());
  useEffect(() => {
    if (isLoading || !profile) return;
    setLocalProfile((prev) => {
      const next = JSON.parse(JSON.stringify(profile));
      for (const key of touched.current) next[key] = prev[key];
      return next;
    });
  }, [isLoading, profile]);
  // Per-provider connection state, keyed by MAIL_PROVIDERS[].id (P0.24 B5). Before
  // this, four msXxx variables and three handlers served one hardcoded card; a second
  // provider would have doubled all of them.
  // The user's own default sending mailbox (0028). Only meaningful when more than one
  // provider is connected; the control below appears only then.
  const [defaultProvider, setDefaultProvider] = useState(null);
  const [savingDefault, setSavingDefault]     = useState(false);
  const [defaultError, setDefaultError]       = useState(null);

  const [connStatus, setConnStatus] = useState({});   // id -> null | 'checking' | status body
  const [connBusy, setConnBusy]     = useState({});   // id -> null | 'connecting' | 'disconnecting'
  const [connError, setConnError]   = useState({});   // id -> string | null

  const setFor = (setter) => (id, value) => setter((prev) => ({ ...prev, [id]: value }));
  const setStatusFor = setFor(setConnStatus);
  const setBusyFor   = setFor(setConnBusy);
  const setErrorFor  = setFor(setConnError);

  // Shared by the section's mount effect and by disconnect, which must re-read the
  // status rather than assume it — the endpoint is idempotent and may report
  // 'already disconnected' for reasons the client cannot see.
  const loadStatus = useCallback(async (id) => {
    setStatusFor(id, 'checking');
    try {
      const r = await apiFetch(`/api/auth/${id}/status`);
      setStatusFor(id, await r.json());
    } catch {
      setStatusFor(id, { connected: false });
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Detach this user's connection. Confirmed first: it is not destructive to data,
  // but it silently stops the features the provider enables until someone
  // reconnects, and that is worth a deliberate click.
  const handleDisconnect = async (provider) => {
    const { id, name, disconnectWarning } = provider;
    if (!window.confirm(`Disconnect ${name}?\n\n${disconnectWarning}`)) return;
    setBusyFor(id, 'disconnecting');
    setErrorFor(id, null);
    try {
      const res = await apiFetch(`/api/auth/${id}/disconnect`, { method: 'POST' });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) {
        setErrorFor(id, body?.error || `Could not disconnect (HTTP ${res.status})`);
      } else {
        await loadStatus(id);
      }
    } catch (e) {
      setErrorFor(id, e.message);
    } finally {
      setBusyFor(id, null);
    }
  };

  // Kicks off the provider's OAuth flow. Not an <a href>: the endpoint needs the
  // Bearer token, which a top-level navigation cannot carry — see startOAuthConnect.
  const handleConnect = async (provider) => {
    const { id } = provider;
    setBusyFor(id, 'connecting');
    setErrorFor(id, null);
    const err = await startOAuthConnect(id);
    // Only reached on failure; on success the browser has already navigated away.
    if (err) { setErrorFor(id, err); setBusyFor(id, null); }
  };

  useEffect(() => {
    if (activeSection !== 'Integrations') return;
    MAIL_PROVIDERS.forEach((p) => loadStatus(p.id));
    // Read the stored default too — the control has to show what is actually set,
    // not an optimistic local guess.
    apiFetch('/api/me/profile')
      .then((r) => r.json())
      .then((d) => { if (d?.success) setDefaultProvider(d.profile?.default_mail_provider ?? null); })
      .catch(() => { /* the control renders unset; saving still works */ });
  }, [activeSection, loadStatus]);

  // Saved immediately on change rather than through the page's save bar: that bar owns
  // the profile form, and a mailbox choice buried in it would be easy to abandon by
  // navigating away — leaving sends refused for a reason the user thought they fixed.
  const chooseDefault = async (id) => {
    const previous = defaultProvider;
    setDefaultProvider(id);            // optimistic; reverted below if the write fails
    setSavingDefault(true);
    setDefaultError(null);
    try {
      const res = await apiFetch('/api/me/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_mail_provider: id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) {
        setDefaultProvider(previous);
        setDefaultError(body?.error || `Could not save (HTTP ${res.status})`);
      }
    } catch (e) {
      setDefaultProvider(previous);
      setDefaultError(e.message);
    } finally {
      setSavingDefault(false);
    }
  };

  const update = (path, value) => {
    // Remember the top-level key so a later provider update cannot overwrite it.
    touched.current.add(path.split('.')[0]);
    setLocalProfile(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  // The four "Company intelligence" fields are TENANT data, not per-browser
  // preferences. They were declared under a comment reading "feeds AI prompts"
  // and fed nothing: updateProfile writes to localStorage, and no handler ever
  // read them. Meanwhile the prompts asserted their own hardcoded version of the
  // same facts. They now POST to /api/tenant/profile, which every persona reads
  // per request (P0.11).
  //
  // The rest of the profile — persona names, timezone, avatar — is genuinely
  // per-user and stays local.
  const handleSave = async () => {
    updateProfile(localProfile);          // local half, unchanged

    // Owner report, 16 Oct 2026: the name on the LightSpeed tenant was set to
    // "George" and Cyrano still said "Sunil". The First/Last name inputs below
    // wrote to local state and nowhere else — this handler's only network call
    // was /api/tenant/profile, which has no name field at all, so the typed name
    // was dropped on every save. The personas read public.users.full_name through
    // displayNameFrom(), which kept returning the value migration 0020 captured
    // from Google at sign-up. Saving looked like it worked; nothing persisted.
    //
    // This is the SECOND defect with this symptom. The 5 Sep one (the long comment
    // on the re-sync effect above) was local form state losing the surname; fixing
    // it left the missing API call untouched, because a form holding the right
    // value still looks correct on screen.
    //
    // Sent BEFORE the tenant block and deliberately NOT inside it:
    //   - `isElevated` gates tenant data, but a person's own name is not an
    //     owner-only field. Gating it would leave every non-owner unable to
    //     correct their name, failing just as silently.
    //   - the tenant save re-derives up to five sets (~11s of Haiku calls). The
    //     name must not wait on that, nor fail with it.
    // Its error is tracked separately so one request failing cannot misreport the
    // other.
    const fullName = [localProfile.firstName, localProfile.lastName]
      .map(s => String(s || '').trim()).filter(Boolean).join(' ');
    setNameError(null);
    if (fullName) {
      try {
        const res = await apiFetch('/api/me/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ full_name: fullName }),
        });
        const body = await res.json().catch(() => null);
        // The endpoint echoes the stored row, so this reports a DROPPED field
        // instead of trusting a 200. pickWritable() silently ignores unknown keys
        // and wrong types — the same posture that let this bug hide.
        if (!res.ok || !body?.success) {
          setNameError(body?.error || `Could not save your name (HTTP ${res.status})`);
        } else if (body.profile && body.profile.full_name !== fullName) {
          setNameError(`Your name was not saved as typed (stored: ${body.profile.full_name || 'empty'}).`);
        }
      } catch (e) {
        setNameError(e.message);
      }
    }

    if (isElevated) {
      setProfileError(null);
      // Owner report, 11 Sep 2026: a save that re-derives four sets took ~11s
      // of sequential Haiku calls, and for all of it the button read "Save
      // Changes" with nothing on screen saying the click had registered. Now
      // it reads "Saving…" and is disabled, so a second click cannot queue a
      // second derivation behind the first.
      setSaving(true);
      try {
        const res = await apiFetch('/api/tenant/profile', {
          method: 'POST',
          body: JSON.stringify({
            firmographics:   localProfile.companyOfferings,
            differentiators: localProfile.competitiveDifferentiators,
            target_profile:  localProfile.targetAccountProfile,
            approach_to_market: localProfile.approachToMarket,
            service_lines:   localProfile.serviceLines || [],
            company_name:       localProfile.companyName,
            company_descriptor: localProfile.companyDescriptor,
            company_vertical:   localProfile.companyVertical,
            company_services:   localProfile.companyServices,   // B77
            // P0.21 task 21.9. Sent ONLY while the tenant is editing the derived
            // terms. Absent from the body means "leave it alone" — the API
            // re-derives or keeps the stored value on its own rules, and a save
            // from any other section must not look like an edit.
            ...(vocabDraft ? { derived_vocabulary: vocabDraft } : {}),
            // 21.10e. Same rule, and it matters more here: these keys are what
            // stored tech_stack rows point at, so an accidental send would stamp
            // edited_at and freeze a set the tenant never chose.
            ...(attrDraft ? { account_attributes: attrDraft } : {}),
            // 21.6d. Same rule again, and the stakes are highest here: these
            // keys are what contacts.practice_area rows point at — migration
            // 0018 moved 21 live rows onto them — so an accidental send would
            // stamp edited_at and freeze a set the tenant never chose.
            ...(areasDraft ? { derived_practice_areas: areasDraft } : {}),
            // 21.8. Same rule. Sent only while editing, because a body that
            // always carried it would stamp edited_at on every save and freeze
            // a set of outbound angles the tenant never chose.
            ...(libsDraft ? { derived_messaging_libraries: libsDraft } : {}),
            // 21.7. Same rule: sent only while editing, so a plain save never
            // stamps edited_at and freezes a feed list the tenant never chose.
            ...(feedsDraft ? { derived_feeds: feedsDraft } : {}),
            deal_parameters: {
              ranges:  localProfile.dealSizeRanges,
              default: localProfile.defaultDealSize,
              tiers:   localProfile.tierDefinitions,
            },
            schema_version: schemaRef.current,
            persona_names: {
              command:        localProfile.personas?.command?.name,
              research:       localProfile.personas?.research?.name,
              strategy:       localProfile.personas?.strategy?.name,
              communications: localProfile.personas?.communications?.name,
              workflow:       localProfile.personas?.workflow?.name,
            },
          }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Save failed');
        setReadiness(json.readiness || null);
        // P0.21 task 21.9. Re-derived server-side when the prose changed, so
        // the panel reflects the save rather than the previous page load.
        setVocabulary(json.derived_vocabulary || null);
        setVocabDraft(null);
        setAttributes(json.account_attributes || null);   // 21.10e
        setAttrDraft(null);
        setAreas(json.derived_practice_areas || null);    // P0.21 task 21.6d
        setAreasDraft(null);
        setLibs(json.derived_messaging_libraries || null); // P0.21 task 21.8
        setLibsDraft(null);
        setFeeds(json.derived_feeds || null);              // P0.21 task 21.7
        setFeedsDraft(null);
      } catch (err) {
        // Say so rather than showing the same "Saved" tick for a write that did
        // not happen — the local half succeeded and the tenant half did not.
        setProfileError(err.message || 'Could not save company details');
        return;
      } finally {
        // `finally`, not after the try: the catch above returns early, and a
        // failed save that left the button stuck on "Saving…" would be worse
        // than the silence this replaces.
        setSaving(false);
      }
    }

    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  // P0.20 task 20.5. Computed by the API, not here: the rule has one
  // definition (api/shared/tenant-readiness.js) and a second copy in JSX is a
  // copy that drifts.
  const [readiness, setReadiness] = useState(null);
  // P0.21 task 21.9. The terms the AI actually searches and scores on, derived
  // from this tenant's own prose. Held separately from localProfile because it
  // is DERIVED rather than typed — the tenant edits the prose, and this is the
  // machine's reading of it, shown so a bad reading is visible and correctable.
  const [vocabulary, setVocabulary] = useState(null);
  const [vocabDraft, setVocabDraft] = useState(null);   // non-null while editing
  // P0.21 task 21.10e. The account-attribute set, same shape of state as the
  // vocabulary above: stored value plus a draft that is non-null only while
  // editing, because a body that always carries it would make every save from
  // any section look like a deliberate edit.
  const [attributes, setAttributes] = useState(null);
  const [attrDraft, setAttrDraft] = useState(null);
  // P0.21 task 21.6d. The practice-area vocabulary, same shape of state again.
  // This one shipped WITHOUT a panel: the derivation, the save ladder, the
  // classifier wiring and migration 0018 all landed while the tenant had no way
  // to see the keys their contact rows point at, or to correct them.
  const [areas, setAreas] = useState(null);
  const [areasDraft, setAreasDraft] = useState(null);
  // P0.21 task 21.8. The messaging libraries — the strategic angles Cyrano
  // writes outbound drafts from. Fourth derived set, same shape of state, and
  // the one the tenant has most right to edit: these are the words that go out
  // under their name.
  const [libs, setLibs] = useState(null);
  const [libsDraft, setLibsDraft] = useState(null);
  // P0.21 task 21.7. The feeds discovered from the vocabulary's trade sources.
  // Fifth derived set, same shape of state; editable because a site that
  // publishes its feed at an odd path is one the owner can paste in by hand.
  const [feeds, setFeeds] = useState(null);
  const [feedsDraft, setFeedsDraft] = useState(null);
  // Snapshot of what this browser held at mount, for the one-time adopt. A ref
  // rather than a dependency: reading localProfile in the effect would re-run it
  // on every keystroke and re-attempt the adopt mid-typing.
  const prevRef = useRef(localProfile);
  // The schema this page was loaded with. Sent on save so the server can refuse
  // a write from a form that predates a migration (task 12.8) — the case the
  // load-time strip cannot see, because the page never reloads.
  const schemaRef = useRef(0);
  const [profileError,  setProfileError]  = useState(null);
  // Separate from profileError: the identity write and the tenant write are two
  // independent requests, and one failing must not report the other as failed.
  const [nameError,     setNameError]     = useState(null);

  // Load the tenant's stored profile and populate the four company fields FROM
  // it. These are tenant data, not browser preferences: what the AI actually has
  // is the truth, and localStorage is at best a stale copy of it.
  //
  // Without this the form showed whatever this browser last held — which, before
  // the defaults were emptied, was another company's profile — while the banner
  // correctly reported the tenant as empty. A filled-in form contradicting the
  // banner is worse than either alone: it invites the user to "save" text they
  // never wrote.
  useEffect(() => {
    if (!isElevated) return;
    let cancelled = false;
    apiFetch('/api/tenant/profile')
      .then(r => r.json())
      .then(j => {
        if (cancelled || !j.success) return;
        setReadiness(j.readiness || null);
        setVocabulary(j.derived_vocabulary || null);   // P0.21 task 21.9
        setAttributes(j.account_attributes || null);  // P0.21 task 21.10e
        setAreas(j.derived_practice_areas || null);   // P0.21 task 21.6d
        setLibs(j.derived_messaging_libraries || null); // P0.21 task 21.8
        setFeeds(j.derived_feeds || null);              // P0.21 task 21.7
        schemaRef.current = j.schema_version || 0;
        const pr = j.profile || {};

        // The tenant is the ONLY authority for these four, so each is set from
        // it unconditionally — including to '' when the tenant has none.
        //
        // The earlier version bailed out on an empty profile (`if (!pr) return`),
        // which left whatever localStorage held on screen. That is exactly the
        // state B52 was reported in and did NOT fix: emptying DEFAULT_PROFILE
        // helps a fresh browser, but any browser that had already saved the old
        // Exavalu defaults kept showing them, because the load path deep-merges
        // stored values OVER the defaults. The form still contradicted the
        // banner, for the same reason, one layer further back.
        const id = j.identity || {};
        const dp = j.deal_parameters || {};
        const pn = j.persona_names || {};

        // ONE-TIME ADOPT (P0.12 task 12.7).
        //
        // Values typed before these fields moved to the tenant are sitting in
        // this browser's localStorage and would otherwise be silently dropped by
        // the strip. If the TENANT has nothing and localStorage has something,
        // hand it to the tenant once.
        //
        // Three guard rails, each load-bearing:
        //   - only when the tenant field is genuinely absent, so a real edit is
        //     never overwritten by an older browser's copy;
        //   - only for owner/admin, which this effect already requires;
        //   - marked on the tenant, so it cannot run twice. An adopt that can
        //     repeat is an adopt that resurrects a stale copy after a real edit —
        //     which is the B52 failure with extra steps.
        if (!j.adopted_from_local) {
          const adopt = {};
          if (!pr.firmographics      && prevRef.current.companyOfferings)           adopt.firmographics      = prevRef.current.companyOfferings;
          if (!pr.differentiators    && prevRef.current.competitiveDifferentiators) adopt.differentiators    = prevRef.current.competitiveDifferentiators;
          if (!pr.target_profile     && prevRef.current.targetAccountProfile)       adopt.target_profile     = prevRef.current.targetAccountProfile;
          if (!pr.approach_to_market && prevRef.current.approachToMarket)           adopt.approach_to_market = prevRef.current.approachToMarket;
          const adoptDescriptor = (!id.descriptor && prevRef.current.companyDescriptor)
            ? prevRef.current.companyDescriptor : null;

          if (Object.keys(adopt).length || adoptDescriptor) {
            apiFetch('/api/tenant/profile', {
              method: 'POST',
              body: JSON.stringify({
                ...adopt,
                ...(adoptDescriptor ? { company_descriptor: adoptDescriptor } : {}),
                adopt_from_local: true,
              }),
            }).then(r => r.json()).then(res => {
              if (res.success) {
                setReadiness(res.readiness || null);
                Object.assign(pr, res.profile || {});
                if (adoptDescriptor) id.descriptor = adoptDescriptor;
              }
            }).catch(() => { /* the fields simply load empty; not worth an error */ });
          }
        }
        setLocalProfile(prev => ({
          ...prev,
          // Identity: the name falls back to prev rather than '' because the
          // sidebar renders it and an empty sidebar reads as a broken page,
          // whereas an absent descriptor correctly renders as nothing.
          companyName:                id.name || prev.companyName || '',
          companyDescriptor:          id.descriptor         || '',
          companyVertical:            id.vertical           || '',
          companyServices:            id.services           || '',   // B77
          companyOfferings:           pr.firmographics      || '',
          competitiveDifferentiators: pr.differentiators    || '',
          targetAccountProfile:       pr.target_profile     || '',
          approachToMarket:           pr.approach_to_market || '',

          // Deal parameters and persona names fall back to `prev` rather than to
          // '' or []: unlike the prose fields, these drive selects and labels
          // that must always have a value. A tenant that has not customised them
          // keeps the product defaults from DEFAULT_PROFILE.
          dealSizeRanges:  (dp.ranges && dp.ranges.length) ? dp.ranges : prev.dealSizeRanges,
          defaultDealSize: dp.default || prev.defaultDealSize,
          tierDefinitions: { ...prev.tierDefinitions, ...(dp.tiers || {}) },
          personas: {
            ...prev.personas,
            command:        { ...prev.personas.command,        ...(pn.command        ? { name: pn.command }        : {}) },
            research:       { ...prev.personas.research,       ...(pn.research       ? { name: pn.research }       : {}) },
            strategy:       { ...prev.personas.strategy,       ...(pn.strategy       ? { name: pn.strategy }       : {}) },
            communications: { ...prev.personas.communications, ...(pn.communications ? { name: pn.communications } : {}) },
            workflow:       { ...prev.personas.workflow,       ...(pn.workflow       ? { name: pn.workflow }       : {}) },
          },
        }));
      })
      .catch(() => { /* the banner simply does not appear; not worth an error */ });
    return () => { cancelled = true; };
  }, [isElevated]);

  const [confirmReset, setConfirmReset] = useState(false);

  const handleReset = () => {
    if (!confirmReset) { setConfirmReset(true); return; }
    resetProfile();
    // Forget what was touched. Otherwise the sync effect would treat the
    // just-cleared fields as user edits and restore them from the form on the
    // next provider update — a reset that visibly undoes itself.
    touched.current.clear();
    setLocalProfile(JSON.parse(JSON.stringify(profile)));
    setConfirmReset(false);
  };

  return (
    <div className="ps-root">
      {/* Header */}
      <div className="ps-header">
        <div className="ps-header-left">
          <h1 className="ps-title">Profile & Settings</h1>
          <p className="ps-subtitle">
            Configure your identity, company intelligence, and AI persona names.
            {' '}<span className="ps-subtitle-emphasis">Everything here shapes how Digital Me works for you.</span>
          </p>
        </div>
        <div className="ps-header-actions">
          {confirmReset ? (
            <>
              <button className="ps-btn-reset ps-btn-confirm" onClick={handleReset}>Confirm reset?</button>
              <button className="ps-btn-cancel" onClick={() => setConfirmReset(false)}>Cancel</button>
            </>
          ) : (
            <button className="ps-btn-reset" onClick={handleReset}>Reset to Defaults</button>
          )}
          <button
            className={`ps-btn-save ${saved ? 'ps-btn-save--saved' : ''} ${saving ? 'ps-btn-save--busy' : ''}`}
            onClick={handleSave}
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="ps-layout">
        {/* Section nav */}
        <nav
          className={`ps-nav ${navScrollable ? 'ps-nav--scrollable' : ''} ${navAtEnd ? 'ps-nav--scrolled-end' : ''}`}
          ref={navRef}
          onScroll={syncNavScroll}
        >
          {sections.map(s => (
            <button
              key={s}
              ref={s === activeSection ? activeTabRef : null}
              className={`ps-nav-item ${activeSection === s ? 'ps-nav-item--active' : ''}`}
              onClick={() => setActiveSection(s)}
            >
              <span className="ps-nav-item-icon">{sectionIcon(s)}</span> {s}
              {/* P0.34 — the Users and Data requests badges moved with their tabs
                  to AdminPage.jsx. Keeping them here would have been dead code
                  keyed on section names this page no longer renders. */}
            </button>
          ))}
          <div className="ps-nav-divider" />
          <div className="ps-nav-note">
            Changes take effect immediately after saving. Your AI personas will use these names everywhere in the platform.
          </div>
        </nav>

        {/* Content */}
        <div className="ps-content">

          {/* -- Identity ------------------------------------------- */}
          {activeSection === 'Identity' && (
            <Section title="Your Identity" subtitle="How Digital Me addresses you and represents you in the platform.">
              {nameError && (
                <div className="ps-empty-profile ps-empty-profile--error" role="alert">
                  {nameError}
                </div>
              )}
              <FieldRow label="First Name" hint="Used in greetings and personalization">
                <input className="ps-input" value={localProfile.firstName} onChange={e => update('firstName', e.target.value)} placeholder="Your first name" />
              </FieldRow>
              <FieldRow label="Last Name" hint="Optional">
                <input className="ps-input" value={localProfile.lastName} onChange={e => update('lastName', e.target.value)} placeholder="Your last name" />
              </FieldRow>
              <FieldRow label="Title / Role" hint="How you describe your role">
                <input className="ps-input" value={localProfile.title} onChange={e => update('title', e.target.value)} placeholder="e.g. Principal, VP Sales, Founder" />
              </FieldRow>
              <FieldRow label="Email" hint="Your work email">
                <input className="ps-input" type="email" value={localProfile.email} onChange={e => update('email', e.target.value)} placeholder="you@company.com" />
              </FieldRow>
              <FieldRow label="Avatar Initial" hint="Single letter shown in your avatar">
                <input className="ps-input ps-input--sm" value={localProfile.avatarInitial} onChange={e => update('avatarInitial', e.target.value.slice(0,1).toUpperCase())} placeholder="G" maxLength={1} />
              </FieldRow>
              <FieldRow label="Timezone" hint="Used by all AIs for scheduling, calendar, and time-aware responses">
                <select
                  className="ps-input"
                  value={localProfile.timezone || 'America/New_York'}
                  onChange={e => { update('timezone', e.target.value); update('timezoneSetByUser', true); }}
                >
                  <optgroup label="United States">
                    <option value="America/New_York">Eastern (New York)</option>
                    <option value="America/Chicago">Central (Chicago)</option>
                    <option value="America/Denver">Mountain (Denver)</option>
                    <option value="America/Phoenix">Mountain - No DST (Phoenix)</option>
                    <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                    <option value="America/Anchorage">Alaska (Anchorage)</option>
                    <option value="Pacific/Honolulu">Hawaii (Honolulu)</option>
                  </optgroup>
                  <optgroup label="Canada">
                    <option value="America/Toronto">Eastern (Toronto)</option>
                    <option value="America/Winnipeg">Central (Winnipeg)</option>
                    <option value="America/Edmonton">Mountain (Edmonton)</option>
                    <option value="America/Vancouver">Pacific (Vancouver)</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="Europe/London">London (GMT/BST)</option>
                    <option value="Europe/Paris">Paris / Berlin (CET)</option>
                    <option value="Europe/Helsinki">Helsinki (EET)</option>
                  </optgroup>
                  <optgroup label="Asia Pacific">
                    <option value="Asia/Dubai">Dubai (GST)</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="Asia/Singapore">Singapore (SGT)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </optgroup>
                </select>
              </FieldRow>
            </Section>
          )}

          {/* -- Company -------------------------------------------- */}
          {activeSection === 'Company' && (
            <Section title="Company Intelligence" subtitle="This context is injected into every AI prompt. The more detail you provide, the more your AI personas will sound like they work for your specific company.">
              <FieldRow label="Company Name" hint="Used by the AI personas wherever they name your company">
                <input className="ps-input" value={localProfile.companyName} onChange={e => update('companyName', e.target.value)} placeholder="Your company name" />
              </FieldRow>
              <FieldRow label="What You Are" hint="How the AI personas describe your company">
                <textarea className="ps-textarea" rows={2} value={localProfile.companyDescriptor} onChange={e => update('companyDescriptor', e.target.value)} placeholder="e.g. a technology consulting firm" />
              </FieldRow>
              {/* P0.20 task 20.2 — distinct from the descriptor above: that is
                  what you ARE, this is who you sell TO. Left blank, the personas
                  speak in general B2B terms rather than naming an industry. */}
              <FieldRow label="Market You Sell Into" hint="Who your buyers are — a short phrase or a sentence, whichever fits. Left blank, the AI avoids industry-specific language rather than guessing">
                {/* A textarea, not a one-line input. These two fields were the
                    only prose fields on the screen rendered as single-line
                    boxes, and the shape of the box is a question: a one-liner
                    reads as "type a label", so a tenant with more to say either
                    truncates or writes a sentence into a box that shows six
                    words of it. Two of three live tenants wrote sentences here.
                    See P0.21 task 21.9 — the terms are derived now, so a fuller
                    answer is strictly better input. */}
                <textarea className="ps-textarea" rows={2} value={localProfile.companyVertical} onChange={e => update('companyVertical', e.target.value)} placeholder="e.g. mid-market manufacturers, or describe your buyers in a sentence" />
              </FieldRow>
              {/* B77. The third distinct fact: descriptor is what you ARE,
                  vertical is who you sell TO, this is what you sell THEM. Read
                  by the prompts and by the news search since P0.10, and settable
                  by nobody until now — the field had three readers and no writer
                  at either layer, so it was empty on every tenant.

                  THE WORK, NOT THE TOOLS (owner, 10 Sep 2026). B77 shipped this
                  hint reading "Platforms and services" with a vendor placeholder,
                  copied from the example in api/shared/company.js. That is wrong,
                  and it taught every tenant to fill the field wrongly. The
                  consumers settle it — sellsLine() renders "<reader> sells
                  {this} to {vertical}", so a vendor list produces "Gary sells
                  Guidewire to P&C insurance carriers", which is false: Guidewire
                  sells Guidewire. research-signal.js calls it "Service lines:"
                  and content/draft.js "Thought leader in". All three want the
                  work performed, not the tools used.

                  Platforms are NOT a separate field on purpose: they are
                  IT-specific, and a hospital or manufacturing tenant has no
                  equivalent. They come from commercial_profile.service_lines.

                  The hint names the search consequence because it is not
                  guessable: blank does not mean "search broadly", it means that
                  search does not run. */}
              <FieldRow label="What You Sell" hint="The work you do — a list or a description, whichever fits. Left blank, the AI skips the platform news search rather than searching for everything">
                <textarea className="ps-textarea" rows={3} value={localProfile.companyServices} onChange={e => update('companyServices', e.target.value)} placeholder="e.g. implementation, support and managed services — the work you do" />
              </FieldRow>
              {/* P0.20 task 20.5. This used to fire only when the WHOLE commercial
                  profile was absent, so a tenant with a profile but no vertical saw
                  nothing while every prompt quietly dropped its industry framing.
                  Now it names the specific fields and what each one costs — the
                  assessment comes from the API so the rule has one definition. */}
              {isElevated && readiness && !readiness.ready && (
                <div className="ps-empty-profile" role="status">
                  {/* THE LEAD DEPENDS ON WHAT IS MISSING, not on whether
                      anything is. "The personas work in general terms rather
                      than naming what you actually sell" was written for an
                      empty tenant. Shown to a tenant with a derived vocabulary
                      — whose personas DO name what they sell — it overstated
                      the gap and read as false. Owner review, 11 Sep 2026. */}
                  {readiness.criticalMissing.length > 0 ? (
                    <>
                      <strong>Your AI is missing context it needs.</strong>{' '}
                      Until then the personas work in general terms rather than
                      naming what you actually sell — deliberately, so they never
                      invent an industry or service lines on your behalf.
                    </>
                  ) : (
                    <>
                      <strong>Your AI is set up.</strong>{' '}
                      The items below would sharpen it further. Nothing is broken
                      without them.
                    </>
                  )}

                  {/* The DERIVED items share one instruction, stated once. Three
                      near-identical paragraphs each saying "open Profile &
                      Settings and press Save" — on the Settings page — buried the
                      one real difference between them, which is that two derive
                      on any save and one needs more prose. Per-item action text
                      is now shown only where it says something the shared line
                      does not. */}
                  {readiness.missing.some(m => m.derived) && (
                    <p className="ps-readiness-shared">
                      The items marked <em>worked out</em> are derived from the fields on
                      this page when you press <strong>Save Changes</strong> — nothing to
                      fill in directly.
                    </p>
                  )}

                  <ul className="ps-readiness-list">
                    {readiness.missing.map(m => (
                      <li key={m.key}>
                        <strong>{m.label}</strong>
                        {m.derived && <span className="ps-readiness-derived"> (worked out)</span>}
                        {' — '}{m.impact}
                        {/* For a derived item the shared line above already said
                            "press Save"; repeat only the part that differs. */}
                        {m.action && !m.derived && (
                          <div className="ps-readiness-action">{m.action}</div>
                        )}
                        {m.action && m.derived && m.actionExtra && (
                          <div className="ps-readiness-action">{m.actionExtra}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {profileError && (
                <div className="ps-empty-profile ps-empty-profile--error" role="alert">
                  Company details were not saved: {profileError}
                </div>
              )}
              <FieldRow label="What You Offer" hint="Your services, products, capabilities -- this feeds AI content generation" tall>
                <textarea className="ps-textarea" rows={5} value={localProfile.companyOfferings} onChange={e => update('companyOfferings', e.target.value)} placeholder="Describe your services, products, and capabilities in detail…" />
              </FieldRow>
              <FieldRow label="Approach to Market" hint="How you sell and engage" tall>
                <textarea className="ps-textarea" rows={4} value={localProfile.approachToMarket} onChange={e => update('approachToMarket', e.target.value)} placeholder="Describe your go-to-market approach, sales motion, engagement style…" />
              </FieldRow>
              <FieldRow label="Competitive Differentiators" hint="What makes you different" tall>
                <textarea className="ps-textarea" rows={4} value={localProfile.competitiveDifferentiators} onChange={e => update('competitiveDifferentiators', e.target.value)} placeholder="What sets you apart from competitors…" />
              </FieldRow>
              <FieldRow label="Ideal Account Profile" hint="What a perfect target account looks like" tall>
                <textarea className="ps-textarea" rows={4} value={localProfile.targetAccountProfile} onChange={e => update('targetAccountProfile', e.target.value)} placeholder="Describe your ideal customer -- size, signals, characteristics…" />
              </FieldRow>

              {/* ── What the AI worked out ──────────────────────────────────
                  P0.21 task 21.6d. These three panels sat in the MIDDLE of this
                  section, after "What You Sell" and before "What You Offer" —
                  which read as though they were derived from the four fields
                  above them and nothing else.

                  They are not. vocabInput (api/tenant/profile.js) reads
                  `vertical`, `services` AND `commercial_profile.service_lines`,
                  and those service lines are what "What You Offer" and "Ideal
                  Account Profile" write. So a tenant editing the last four
                  fields was editing inputs to panels rendered ABOVE them, with
                  nothing on screen to suggest the connection.

                  Below every field that feeds them, the section now reads in the
                  order the data actually flows: what you told us, then what the
                  AI made of it.

                  The order WITHIN the three is deliberate and unchanged —
                  attributes decide what is recorded about an ACCOUNT, practice
                  areas decide what a PERSON is filed under, the vocabulary
                  decides which STORIES score. Broad to narrow. */}
              {isElevated && (attributes?.groups?.length > 0 || areas?.areas?.length > 0 || vocabulary) && (
                <div className="ps-derived-head">
                  <h3 className="ps-derived-title">What your AI worked out</h3>
                  <p className="ps-derived-sub">
                    Read from everything above when you save. Edit anything that came out wrong —
                    an edited set is kept as-is and never re-derived.
                  </p>
                </div>
              )}

              {isElevated && attributes?.groups?.length > 0 && (
                <AttributesPanel
                  attributes={attributes}
                  draft={attrDraft}
                  onEdit={() => setAttrDraft({
                    panel_label: attributes.panel_label || '',
                    item_label:  attributes.item_label || '',
                    groups:      (attributes.groups || []).map(g => ({ ...g })),
                  })}
                  onChange={next => setAttrDraft(next)}
                  onCancel={() => setAttrDraft(null)}
                />
              )}

              {isElevated && areas?.areas?.length > 0 && (
                <PracticeAreasPanel
                  areas={areas}
                  draft={areasDraft}
                  onEdit={() => setAreasDraft({
                    areas: (areas.areas || []).map(a => ({ ...a })),
                  })}
                  onChange={next => setAreasDraft(next)}
                  onCancel={() => setAreasDraft(null)}
                />
              )}

              {/* P0.21 task 21.8. After practice areas, before the vocabulary:
                  attributes decide what is recorded about an ACCOUNT, practice
                  areas what a PERSON is filed under, this what OUTREACH is
                  written from, and the vocabulary which STORIES score. Rendered
                  only when there is a set — an unconfigured tenant gets the
                  readiness banner, never an empty panel. */}
              {isElevated && libs?.libraries?.length > 0 && (
                <MessagingLibrariesPanel
                  libs={libs}
                  draft={libsDraft}
                  onEdit={() => setLibsDraft({
                    libraries: (libs.libraries || []).map(l => ({
                      ...l,
                      angles:        [...(l.angles || [])],
                      tensionPoints: [...(l.tensionPoints || [])],
                      match:         [...(l.match || [])],
                    })),
                  })}
                  onChange={next => setLibsDraft(next)}
                  onCancel={() => setLibsDraft(null)}
                />
              )}

              {isElevated && vocabulary && (
                <VocabularyPanel
                  vocabulary={vocabulary}
                  draft={vocabDraft}
                  onEdit={() => setVocabDraft({
                    market_terms:  [...(vocabulary.market_terms || [])],
                    platforms:     [...(vocabulary.platforms || [])],
                    buyer_roles:   [...(vocabulary.buyer_roles || [])],
                    trade_sources: [...(vocabulary.trade_sources || [])],
                  })}
                  onChange={(key, value) => setVocabDraft(d => ({ ...d, [key]: value }))}
                  onCancel={() => setVocabDraft(null)}
                />
              )}

              {/* P0.21 task 21.7. After the vocabulary, because it is derived
                  FROM the vocabulary's trade sources — reading top to bottom,
                  the tenant sees the sources and then the feeds found on them.
                  Rendered only when there is a set: no feeds means the
                  readiness panel says so, never an empty box. */}
              {isElevated && feeds?.feeds?.length > 0 && (
                <FeedsPanel
                  feeds={feeds}
                  draft={feedsDraft}
                  onEdit={() => setFeedsDraft({ feeds: (feeds.feeds || []).map(f => ({ ...f })) })}
                  onChange={next => setFeedsDraft(next)}
                  onCancel={() => setFeedsDraft(null)}
                />
              )}
            </Section>
          )}

          {/* -- AI Personas ----------------------------------------- */}
          {activeSection === 'AI Personas' && (
            <Section
              title="AI Persona Names"
              subtitle="Rename your AI team to anything that resonates with you. The stock names are the defaults -- your customers can personalize these to fit their culture and brand."
            >
              <div className="ps-persona-grid">
                {[
                  { role: 'command',        icon: '🤖', label: 'Command AI',       desc: 'Your executive AI. Always present, orchestrates everything. The one you talk to directly.',       path: 'personas.command.name' },
                  { role: 'research',       icon: '🔭', label: 'Research AI',      desc: 'Intelligence analyst. Owns research, signals, lead scoring, and overnight monitoring.',            path: 'personas.research.name' },
                  { role: 'strategy',       icon: '♟️', label: 'Strategy AI',      desc: 'Strategist. Deal coaching, targeting, competitive framing, and prioritization.',                   path: 'personas.strategy.name' },
                  { role: 'communications', icon: '✍️', label: 'Communications AI', desc: 'Writer. Emails, LinkedIn articles, proposals, and talking points -- all in your voice.',           path: 'personas.communications.name' },
                  { role: 'workflow',       icon: '📅', label: 'Workflow AI',       desc: 'Executive assistant. Calendar, tasks, reminders, and the daily brief.',                          path: 'personas.workflow.name' },
                ].map(({ role, icon, label, desc, path }) => (
                  <div key={role} className={`ps-persona-card ps-persona-card--${role}`}>
                    <div className="ps-persona-card-top">
                      <span className="ps-persona-icon">{icon}</span>
                      <div>
                        <div className="ps-persona-role">{label}</div>
                        <p className="ps-persona-desc">{desc}</p>
                      </div>
                    </div>
                    <div className="ps-persona-name-row">
                      <label className="ps-persona-name-label">Name</label>
                      <input
                        className="ps-input ps-persona-name-input"
                        value={getNestedValue(localProfile, path)}
                        onChange={e => update(path, e.target.value)}
                        placeholder={`e.g. ${defaultPersonaName(role)}`}
                      />
                    </div>
                    <div className="ps-persona-preview">
                      Currently: <strong>{getNestedValue(localProfile, path) || defaultPersonaName(role)}</strong>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ps-persona-note">
                <span className="ps-persona-note-icon">💡</span>
                <div>
                  <strong>Tip:</strong> Names that carry meaning stick better. Our defaults (Jarvis, Selma, Victor, Cyrano) were chosen because each name reflects what the persona does. Your customers are free to name theirs anything -- Max, Friday, Alex, Scout -- whatever fits their culture.
                </div>
              </div>
            </Section>
          )}

          {/* -- Persona briefs (P0.17 17.10b) ------------------------ */}
          {/* Owner/admin only, and gated here as well as in the API. An edit
              changes what every user of the tenant gets from every persona,
              which is the same all-or-nothing shape as tenant assignment. The
              API's requireRole is the real gate; this keeps a member from being
              shown a screen they cannot use. */}
          {activeSection === 'AI Personas' && isElevated && (
            <Section
              title="Persona Briefs"
              subtitle="What each AI is told before it answers. Editing a brief changes how that persona speaks and what it prioritises, for everyone in your organisation."
            >
              <PromptEditorPanel />
            </Section>
          )}

          {/* -- Deal Settings --------------------------------------- */}
          {/* B81. The subtitle read "These defaults are used by the AI scoring
              system and throughout the platform" while NOTHING read these values
              — one producer, zero consumers. A tenant following that sentence
              would tune numbers that changed nothing. The values now reach the
              persona prompts, and the subtitle says what they actually do. */}
          {activeSection === 'Deal Settings' && (
            <Section title="Deal Parameters" subtitle="Your deal size bands and what earns an account priority. The AI personas read these when they size an opportunity or argue about which accounts matter.">
              <FieldRow label="Default Deal Size" hint="Pre-selected when creating new opportunities">
                <select className="ps-select" value={localProfile.defaultDealSize} onChange={e => update('defaultDealSize', e.target.value)}>
                  {localProfile.dealSizeRanges.map(r => <option key={r}>{r}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Tier 1 Definition" hint="Your top-priority account criteria" tall>
                <textarea className="ps-textarea" rows={3} value={localProfile.tierDefinitions?.tier1 || ''} onChange={e => update('tierDefinitions.tier1', e.target.value)} placeholder="What makes an account Tier 1 for you?" />
              </FieldRow>
              <FieldRow label="Tier 2 Definition" hint="Mid-priority account criteria" tall>
                <textarea className="ps-textarea" rows={3} value={localProfile.tierDefinitions?.tier2 || ''} onChange={e => update('tierDefinitions.tier2', e.target.value)} placeholder="What makes an account Tier 2?" />
              </FieldRow>
              <FieldRow label="Tier 3 Definition" hint="Watch list criteria" tall>
                <textarea className="ps-textarea" rows={3} value={localProfile.tierDefinitions?.tier3 || ''} onChange={e => update('tierDefinitions.tier3', e.target.value)} placeholder="What makes an account Tier 3?" />
              </FieldRow>
            </Section>
          )}

          {/* -- Training ------------------------------------------ */}
          {activeSection === 'Training' && (
            <Section title="AI Training Center" subtitle="Upload documents to make your AI team smarter. Company training feeds Victor, Selma, and Cyrano. Voice training teaches Cyrano your writing style.">
              <TrainingSection />
            </Section>
          )}

          {/* -- Data (CSV import/export) --------------------------- */}
          {activeSection === 'Data' && (
            <Section
              title="Import History"
              subtitle="Every CSV import this workspace has run, newest first. To import or export, use ⇅ Data in the header of the Accounts, Contacts, Leads, Pipeline or Tasks page."
            >
              <DataTransferSection />
            </Section>
          )}

          {/* -- Integrations -------------------------------------- */}


          {/* -- Privacy (everyone) ----------------------------------- */}
          {activeSection === 'Privacy' && (
            <Section
              title="Privacy"
              subtitle="Our Privacy Policy and Terms, and how to have your data deleted."
            >
              <PrivacySection />
            </Section>
          )}



          {activeSection === 'Integrations' && (
            <Section title="Integrations" subtitle="Connect your mailbox — Microsoft 365 or Google — to send and receive email as yourself. Signing in with Google or Microsoft is a different thing: it says who you are, and grants no mailbox access. Connect here as well.">

              {/* Outcome of a just-completed OAuth round trip. Before this, both success
                  and failure returned the user to the dashboard with a query param
                  nothing read — a failed connection looked like a page refresh. */}
              {authOutcome && (
                <div className={`ps-auth-outcome ps-auth-outcome--${authOutcome.ok ? 'ok' : 'error'}`}
                     role={authOutcome.ok ? 'status' : 'alert'}>
                  {authOutcome.ok
                    ? `✓ ${PROVIDER_LABELS[authOutcome.provider] || 'Account'} connected.`
                    : `Could not connect ${PROVIDER_LABELS[authOutcome.provider] || 'account'} — ${authOutcome.message}`}
                  <button type="button" className="ps-auth-outcome-dismiss"
                          onClick={() => setAuthOutcome(null)} aria-label="Dismiss">×</button>
                </div>
              )}

              {MAIL_PROVIDERS.map((provider) => (
                <IntegrationCard
                  key={provider.id}
                  provider={provider}
                  status={connStatus[provider.id] ?? null}
                  busy={connBusy[provider.id] ?? null}
                  error={connError[provider.id] ?? null}
                  onConnect={() => handleConnect(provider)}
                  onDisconnect={() => handleDisconnect(provider)}
                />
              ))}

              {/* Which mailbox sends (0028). Shown only when the choice is real —
                  with one provider connected there is nothing to decide, and with none
                  the cards above are the thing to act on. */}
              {MAIL_PROVIDERS.filter((p) => connStatus[p.id]?.connected).length > 1 && (
                <div className="ps-integration-config">
                  <h4 className="ps-integration-config-title">Send my mail from</h4>
                  <p className="ps-integration-config-desc">
                    Both mailboxes are connected, so DigitalMe needs to know which one your
                    outgoing mail should leave from. Replies always go out through the mailbox
                    the original arrived in, whatever you choose here.
                  </p>
                  {MAIL_PROVIDERS.filter((p) => connStatus[p.id]?.connected).map((p) => (
                    <label key={p.id} className="ps-radio-row">
                      <input type="radio" name="default-mail-provider"
                             checked={defaultProvider === p.id}
                             disabled={savingDefault}
                             onChange={() => chooseDefault(p.id)} />
                      <span>{p.name}{connStatus[p.id]?.email ? ` — ${connStatus[p.id].email}` : ''}</span>
                    </label>
                  ))}
                  {!defaultProvider && (
                    <p className="ps-integration-status ps-integration-status--disconnected">
                      Not set — sending a new email will ask you to choose.
                    </p>
                  )}
                  {defaultError && (
                    <p className="ps-integration-error" role="alert">{defaultError}</p>
                  )}
                </div>
              )}

              {/* Teams — disabled pending per-tenant configuration (B51).
                  The panel here used to tell the reader to set TEAMS_TEAM_ID and
                  TEAMS_CHANNEL_ID in Vercel. Those are deployment-wide env vars: every
                  tenant would post into the same channel. Saying so plainly beats an
                  instruction that quietly does the wrong thing on a second tenant. */}
              {connStatus.microsoft?.connected && (
                <div className="ps-integration-config ps-integration-config--disabled">
                  <h4 className="ps-integration-config-title">Teams notifications — unavailable</h4>
                  <p className="ps-integration-config-desc">
                    Teams notifications are turned off. The destination team and channel are
                    currently deployment-wide settings rather than per-workspace ones, so every
                    workspace would post into the same channel. Outlook, Calendar and
                    SharePoint are unaffected.
                  </p>
                </div>
              )}

            </Section>
          )}

          {/* Save bar */}
          <div className="ps-save-bar">
            <span className="ps-save-hint">
              {saving
                ? 'Saving, and working out what your AI should know from what you wrote — a few seconds.'
                : 'Changes are saved to your profile and take effect immediately.'}
            </span>
            <button
              className={`ps-btn-save ${saved ? 'ps-btn-save--saved' : ''} ${saving ? 'ps-btn-save--busy' : ''}`}
              onClick={handleSave}
              disabled={saving}
              aria-busy={saving}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// -- Sub-components ------------------------------------------------------------
/**
 * P0.21 task 21.9 — what the AI reads, shown back to the tenant.
 *
 * The tenant writes prose; the code that searches the web and scores signals
 * needs terms. A cheap model bridges the two on save. This panel is the part
 * that keeps that honest: without it the derivation is invisible, and a tenant
 * whose market came out as ["cio", "cto"] would see only a quiet feed and have
 * nothing to connect it to.
 *
 * Read-only by default, because in the normal case there is nothing to do. The
 * edit path exists for the case the model got it wrong, and an edited list is
 * never silently re-derived over — see isEdited in api/shared/derive-vocabulary.
 */

/**
 * The account-attribute set, shown back and editable (P0.21 task 21.10e).
 *
 * The sibling of VocabularyPanel below, and the reasoning is the same: a
 * derivation nobody can see is a derivation nobody can correct. What differs is
 * the cost of being wrong. The vocabulary decides which stories score highly;
 * this decides what the product RECORDS about every account, what the nightly
 * research run asks, and what the account panel renders.
 *
 * KEYS ARE NOT EDITABLE, deliberately. `key` is a foreign key in everything but
 * name — tech_stack rows carry it, the panel groups by it, the research prompt
 * asks for it. Renaming one orphans every row that points at the old value, so
 * the label and the hint are editable and the key is shown as the fixed thing it
 * is. Adding and removing groups is safe and allowed; a removed group's rows
 * fall to the Uncategorised tile rather than vanishing.
 */
function AttributesPanel({ attributes, draft, onEdit, onChange, onCancel }) {
  const editing = Boolean(draft);
  const shown = draft || attributes;
  const groups = shown?.groups || [];

  return (
    <div className="ps-vocab">
      <div className="ps-vocab-head">
        <strong>What your AI researches about an account</strong>
        {!editing && (
          <button type="button" className="ps-vocab-edit" onClick={onEdit}>Edit</button>
        )}
        {editing && (
          <button type="button" className="ps-vocab-edit" onClick={onCancel}>Cancel</button>
        )}
      </div>

      <p className="ps-vocab-note">
        Worked out from what you wrote above.{' '}
        {attributes?.edited_at
          ? 'You have edited these, so they are kept as-is when you save.'
          : 'They are refreshed whenever you change what you sell.'}
      </p>

      {editing && (
        <div className="ps-vocab-group">
          <div className="ps-vocab-label">
            Tab name
            <span className="ps-vocab-hint"> — what this section is called on an account</span>
          </div>
          <input
            className="ps-input"
            value={shown?.panel_label || ''}
            onChange={e => onChange({ ...shown, panel_label: e.target.value })}
          />
          <div className="ps-vocab-label" style={{ marginTop: 10 }}>
            Each entry is called
            <span className="ps-vocab-hint"> — reads as “Add …” on the button</span>
          </div>
          <input
            className="ps-input"
            value={shown?.item_label || ''}
            onChange={e => onChange({ ...shown, item_label: e.target.value })}
          />
        </div>
      )}

      {groups.map((g, i) => (
        <div className="ps-vocab-group" key={g.key}>
          <div className="ps-vocab-label">
            {editing ? (
              <input
                className="ps-input"
                value={g.label}
                onChange={e => onChange({
                  ...shown,
                  groups: groups.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                })}
              />
            ) : g.label}
            {/* The key is shown, never edited: rows point at it. */}
            <span className="ps-vocab-hint"> — {g.key}</span>
          </div>
          {editing ? (
            <textarea
              className="ps-textarea ps-vocab-input"
              rows={2}
              value={g.hint || ''}
              placeholder="what a researcher should look for"
              onChange={e => onChange({
                ...shown,
                groups: groups.map((x, j) => (j === i ? { ...x, hint: e.target.value } : x)),
              })}
            />
          ) : (
            g.hint && <div className="ps-vocab-terms"><span className="ps-vocab-term">{g.hint}</span></div>
          )}
        </div>
      ))}

      {editing && (
        <p className="ps-vocab-note ps-vocab-note--warn">
          Save the page to apply. The short code beside each name is what already-recorded
          entries are filed under, so it stays fixed — rename the label freely.
          These descriptions are sent to the AI as the questions it researches, so a vague
          one produces vague answers.
        </p>
      )}
    </div>
  );
}

// P0.21 task 21.6d — the practice-area vocabulary, shown back and editable.
//
// WHY THIS PANEL EXISTS, AND WHY IT IS LATE
// The derivation, the four-branch save ladder, the classifier wiring and a
// production migration all shipped before this did. So the tenant whose contacts
// were re-filed onto these keys could not see them, could not correct them, and
// was never told the set existed.
//
// THE KEY IS A FOREIGN KEY IN EVERYTHING BUT NAME — more literally here than in
// the attributes panel. contacts.practice_area stores this exact string, and
// migration 0018 moved 21 live rows onto these values. Renaming a key orphans
// every contact filed under the old one: the panel still renders, the save still
// succeeds, and those contacts quietly stop matching anything.
//
// So the label and hint are editable and the key is not. Same rule as
// AttributesPanel, same reason, higher stakes.
function PracticeAreasPanel({ areas, draft, onEdit, onChange, onCancel }) {
  const editing = Boolean(draft);
  const shown = draft || areas;
  const list = shown?.areas || [];

  return (
    <div className="ps-vocab">
      <div className="ps-vocab-head">
        <strong>The subject areas your contacts work on</strong>
        {!editing && (
          <button type="button" className="ps-vocab-edit" onClick={onEdit}>Edit</button>
        )}
        {editing && (
          <button type="button" className="ps-vocab-edit" onClick={onCancel}>Cancel</button>
        )}
      </div>

      <p className="ps-vocab-note">
        Worked out from what you wrote above. Every contact you save is filed under one of
        these.{' '}
        {areas?.edited_at
          ? 'You have edited these, so they are kept as-is when you save.'
          : 'They are refreshed whenever you change what you sell.'}
      </p>

      {list.map((a, i) => (
        <div className="ps-vocab-group" key={a.key}>
          <div className="ps-vocab-label">
            {editing ? (
              <input
                className="ps-input"
                value={a.label}
                onChange={e => onChange({
                  ...shown,
                  areas: list.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                })}
              />
            ) : a.label}
            {/* Shown, never edited: contact rows are filed under this. */}
            <span className="ps-vocab-hint"> — {a.key}</span>
          </div>
          {editing ? (
            <textarea
              className="ps-textarea ps-vocab-input"
              rows={2}
              value={a.hint || ''}
              placeholder="what kind of role belongs here"
              onChange={e => onChange({
                ...shown,
                areas: list.map((x, j) => (j === i ? { ...x, hint: e.target.value } : x)),
              })}
            />
          ) : (
            a.hint && <div className="ps-vocab-terms"><span className="ps-vocab-term">{a.hint}</span></div>
          )}
        </div>
      ))}

      {editing && (
        <p className="ps-vocab-note ps-vocab-note--warn">
          Save the page to apply. The short code beside each name is what your existing
          contacts are filed under, so it stays fixed — rename the label freely.
          Each description tells the AI which roles belong in that area, so a vague one
          produces vague classifications. An area with no description is dropped.
        </p>
      )}
    </div>
  );
}

// P0.21 task 21.8 — the messaging libraries, shown back and editable.
//
// WHAT THIS IS
// The strategic angle Cyrano writes an outbound draft FROM: a core insight, the
// angles that reframe the buyer's problem, the tension points nobody has said
// out loud, and the match terms that route a news signal here. Derived from the
// tenant's own prose; the six packaged P&C libraries it replaces are gone, with
// no fallback, because serving one tenant's angles to everyone else was the
// defect.
//
// WHY THE TENANT MUST BE ABLE TO EDIT THIS ONE ABOVE ALL
// A derived practice area mislabels a contact. A derived messaging angle goes
// OUT — into a message, over the tenant's name, to their prospect. If the model
// produced a core insight the owner would never say, the fix has to be a text
// box here, not a support ticket.
//
// FOUR EDITABLE FIELDS, NOT TWO. The sibling panels edit a label and a hint.
// This one also edits the angles, the tension points and the match terms, and
// the last of those CHANGES BEHAVIOUR: match terms decide which library a
// signal routes to. They are matched on word boundaries, so a term like "ai"
// only fires on the word "ai" — the packaged regex used to fire on "said" and
// "detail", and that is fixed by the derivation, not undone by an edit.
//
// The key is shown but never editable — not because rows point at it (nothing
// stores it) but because the router returns it and a rename mid-edit would
// leave a draft in flight pointing at a key that no longer exists.
function MessagingLibrariesPanel({ libs, draft, onEdit, onChange, onCancel }) {
  const editing = Boolean(draft);
  const shown = draft || libs;
  const list = shown?.libraries || [];

  // One line per item in a textarea, matching VocabularyPanel's idiom — the
  // owner already knows that convention from the box directly below this one.
  const lines = (arr) => (arr || []).join('\n');
  const fromLines = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);
  const setField = (i, field, value) => onChange({
    ...shown,
    libraries: list.map((x, j) => (j === i ? { ...x, [field]: value } : x)),
  });

  return (
    <div className="ps-vocab">
      <div className="ps-vocab-head">
        <strong>The angles your outreach is written from</strong>
        {!editing && (
          <button type="button" className="ps-vocab-edit" onClick={onEdit}>Edit</button>
        )}
        {editing && (
          <button type="button" className="ps-vocab-edit" onClick={onCancel}>Cancel</button>
        )}
      </div>

      <p className="ps-vocab-note">
        Worked out from what you wrote above. When a news signal arrives about an account,
        one of these is chosen and Cyrano writes from it.{' '}
        {libs?.edited_at
          ? 'You have edited these, so they are kept as-is when you save.'
          : 'They are refreshed whenever you change what you sell.'}
      </p>

      {list.map((l, i) => (
        <div className="ps-vocab-group" key={l.key}>
          <div className="ps-vocab-label">
            {editing ? (
              <input
                className="ps-input"
                value={l.label}
                onChange={e => setField(i, 'label', e.target.value)}
              />
            ) : l.label}
            {/* Shown, never edited: the router returns this key. */}
            <span className="ps-vocab-hint"> — {l.key}</span>
          </div>

          {editing ? (
            <>
              <p className="ps-vocab-guide">
                Core insight — one sentence that corrects the obvious diagnosis. “The decision
                is not the platform, it is the implementation risk.” Not a description of the
                market; the buyer already knows that.
              </p>
              <textarea
                className="ps-textarea ps-vocab-input"
                rows={2}
                value={l.coreInsight || ''}
                onChange={e => setField(i, 'coreInsight', e.target.value)}
              />
              <p className="ps-vocab-guide">
                Angles — one per line. Short assertions that reframe the buyer's problem.
                None should be a pitch or mention your company.
              </p>
              <textarea
                className="ps-textarea ps-vocab-input"
                rows={4}
                value={lines(l.angles)}
                onChange={e => setField(i, 'angles', fromLines(e.target.value))}
              />
              <p className="ps-vocab-guide">
                Tension points — one per line. What is quietly going wrong inside the buyer's
                organisation that they have not said out loud yet.
              </p>
              <textarea
                className="ps-textarea ps-vocab-input"
                rows={3}
                value={lines(l.tensionPoints)}
                onChange={e => setField(i, 'tensionPoints', fromLines(e.target.value))}
              />
              <p className="ps-vocab-guide">
                Match terms — one per line, lowercase. Named products and the words this problem
                is described with in a headline. A signal mentioning one of these routes here, so
                a term too general (“system”, “data”) sends every story to this library.
              </p>
              <textarea
                className="ps-textarea ps-vocab-input"
                rows={3}
                value={lines(l.match)}
                onChange={e => setField(i, 'match', fromLines(e.target.value))}
              />
            </>
          ) : (
            <>
              {l.coreInsight && (
                <div className="ps-vocab-terms"><span className="ps-vocab-term">{l.coreInsight}</span></div>
              )}
              {(l.match || []).length > 0 && (
                <div className="ps-vocab-terms">
                  {l.match.map(t => <span className="ps-vocab-term" key={t}>{t}</span>)}
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {editing && (
        <p className="ps-vocab-note ps-vocab-note--warn">
          Save the page to apply. These words go into outreach sent under your name. A library
          with no core insight, fewer than two angles or tension points, or no match terms is
          dropped when you save.
        </p>
      )}
    </div>
  );
}

function VocabularyPanel({ vocabulary, draft, onEdit, onChange, onCancel }) {
  const editing = Boolean(draft);
  const shown = draft || vocabulary;

  // P0.21 task 21.6d. `guide` is shown only while EDITING, and says what makes a
  // good entry for THIS box. The hints alone say what each list is used for,
  // which does not help someone staring at four identical textareas wondering
  // which one matters.
  const GROUPS = [
    { key: 'market_terms',  label: 'Market',        hint: 'used to score how relevant a story is to you',
      guide: 'How your buyers would be described in a headline — “mid-market manufacturers”, '
           + '“regional hospitals”. Not single common words: “technology” matches everything.' },
    { key: 'platforms',     label: 'Platforms',     hint: 'searched for by name in the news',
      guide: 'Named products only — Salesforce, SAP, Workday, ServiceNow. This is the highest-value '
           + 'box: a product name in a headline is the strongest buying signal there is. '
           + 'Capability phrases like “digital transformation” match every consulting article.' },
    { key: 'buyer_roles',   label: 'Buyer roles',   hint: 'raise a signal when named in a story',
      guide: 'Job titles that sign off on what you sell — “cio”, “vp of operations”. Also used '
           + 'to pick which contacts to search for at an account.' },
    { key: 'trade_sources', label: 'Trade sources', hint: 'trusted more than a general news site',
      guide: 'Domain names only, one per line — “modernhealthcare.com”. Leave blank if you are '
           + 'not sure: a wrong domain quietly inflates the score of an unrelated source.' },
  ];

  // Nothing derived and nothing to edit — say why rather than showing four
  // empty rows, which reads as a broken panel.
  const isEmpty = GROUPS.every(g => !(shown?.[g.key] || []).length);
  if (isEmpty && !editing) {
    return (
      <div className="ps-vocab" role="status">
        <div className="ps-vocab-head">
          <strong>What your AI searches for</strong>
        </div>
        <p className="ps-vocab-note">
          Nothing yet — fill in <em>Market You Sell Into</em> and <em>What You Sell</em> above,
          then save. The AI works out its search terms from what you write.
        </p>
      </div>
    );
  }

  return (
    <div className="ps-vocab">
      <div className="ps-vocab-head">
        <strong>What your AI searches for</strong>
        {!editing && (
          <button type="button" className="ps-vocab-edit" onClick={onEdit}>
            Edit
          </button>
        )}
        {editing && (
          <button type="button" className="ps-vocab-edit" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      <p className="ps-vocab-note">
        Worked out from what you wrote above.{' '}
        {vocabulary.edited_at
          ? 'You have edited these, so they are kept as-is when you save.'
          : 'They are refreshed whenever you change what you sell.'}
        {editing && ' One per line.'}
      </p>

      {GROUPS.map(({ key, label, hint, guide }) => {
        const terms = shown?.[key] || [];
        if (!editing && !terms.length) return null;
        return (
          <div className="ps-vocab-group" key={key}>
            <div className="ps-vocab-label">
              {label}
              <span className="ps-vocab-hint"> — {hint}</span>
            </div>
            {editing && <p className="ps-vocab-guide">{guide}</p>}
            {editing ? (
              <textarea
                className="ps-textarea ps-vocab-input"
                rows={3}
                value={terms.join('\n')}
                onChange={e => onChange(key, e.target.value.split('\n').map(t => t.trim()).filter(Boolean))}
                placeholder={key === 'trade_sources' ? 'example.com' : 'one term per line'}
              />
            ) : (
              <div className="ps-vocab-terms">
                {terms.map(t => <span className="ps-vocab-term" key={t}>{t}</span>)}
              </div>
            )}
          </div>
        );
      })}

      {editing && (
        <p className="ps-vocab-note ps-vocab-note--warn">
          Save the page to apply. These are matched inside headlines, so a very
          general word — “technology”, “business” — will match almost everything
          and make the feed noisier, not better.
        </p>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="ps-section">
      <div className="ps-section-header">
        <h2 className="ps-section-title">{title}</h2>
        {subtitle && <p className="ps-section-sub">{subtitle}</p>}
      </div>
      <div className="ps-section-body">{children}</div>
    </div>
  );
}

function FieldRow({ label, hint, tall, children }) {
  return (
    <div className={`ps-field-row ${tall ? 'ps-field-row--tall' : ''}`}>
      <div className="ps-field-label-col">
        <label className="ps-field-label">{label}</label>
        {hint && <span className="ps-field-hint">{hint}</span>}
      </div>
      <div className="ps-field-input-col">{children}</div>
    </div>
  );
}

// -- Helpers -------------------------------------------------------------------
function sectionIcon(s) {
  // P0.34 — Users, Logs, Platform and Data requests dropped with their tabs.
  // An icon for a section this page cannot render is dead, and worse than dead:
  // it reads as evidence the tab still belongs here. AdminPage.jsx carries its
  // own icons on ADMIN_SECTIONS.
  return { Identity: '👤', Company: '🏢', 'AI Personas': '🤖', 'Deal Settings': '⚙️', 'Training': '🧠', 'Data': '📊', 'Integrations': '🔗', Privacy: '🛡️' }[s] || '';
}

function defaultPersonaName(role) {
  // Must stay in step with DEFAULTS in api/shared/personas.js.
  return { command: 'Jarvis', research: 'Selma', strategy: 'Victor', communications: 'Cyrano', workflow: 'Quinn' }[role] || '';
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((o, k) => o?.[k], obj) ?? '';
}

// P0.21 task 21.7. The feeds the intel feed reads, shown back and editable.
//
// Defined at the END of the file, deliberately: the sibling panel tests slice
// this source from their own component to the NEXT `function`, and a panel
// inserted between two of them silently widens the earlier one's slice (it
// happened twice on 11 Sep). Nothing follows this, so nothing widens.
//
// Two editable fields per row — name and URL — plus remove and add. The URL is
// the one that changes behaviour: it is what gets fetched. The domain is shown,
// never edited; the server derives it from the URL on save.
function FeedsPanel({ feeds, draft, onEdit, onChange, onCancel }) {
  const editing = Boolean(draft);
  const shown = draft || feeds;
  const list = shown?.feeds || [];

  const setField = (i, field, value) => onChange({
    ...shown,
    feeds: list.map((x, j) => (j === i ? { ...x, [field]: value } : x)),
  });
  const remove = (i) => onChange({ ...shown, feeds: list.filter((_, j) => j !== i) });
  const add = () => onChange({ ...shown, feeds: [...list, { name: '', url: '', domain: '' }] });

  return (
    <div className="ps-vocab">
      <div className="ps-vocab-head">
        <strong>The trade publications your intel feed reads</strong>
        {!editing && (
          <button type="button" className="ps-vocab-edit" onClick={onEdit}>Edit</button>
        )}
        {editing && (
          <button type="button" className="ps-vocab-edit" onClick={onCancel}>Cancel</button>
        )}
      </div>

      <p className="ps-vocab-note">
        Found on the trade sources listed in your vocabulary above — each site was checked
        for a real feed and named from its own title.{' '}
        {feeds?.edited_at
          ? 'You have edited these, so they are kept as-is when you save.'
          : 'They are refreshed whenever your trade sources change.'}
      </p>

      {list.map((f, i) => (
        <div className="ps-vocab-group" key={f.url || i}>
          {editing ? (
            <>
              <input
                className="ps-input"
                placeholder="Publication name"
                value={f.name || ''}
                onChange={e => setField(i, 'name', e.target.value)}
              />
              <input
                className="ps-input ps-vocab-input"
                placeholder="https://example.com/feed/"
                value={f.url || ''}
                onChange={e => setField(i, 'url', e.target.value)}
              />
              <button type="button" className="ps-vocab-edit" onClick={() => remove(i)}>Remove</button>
            </>
          ) : (
            <div className="ps-vocab-label">
              {f.name || f.domain}
              {/* Shown, never edited: the server derives it from the URL. */}
              <span className="ps-vocab-hint"> — {f.domain}</span>
              <div className="ps-vocab-hint">{f.url}</div>
            </div>
          )}
        </div>
      ))}

      {editing && (
        <button type="button" className="ps-vocab-edit" onClick={add}>Add a feed</button>
      )}
    </div>
  );
}
